<template>
  <div
    v-if="shouldShowNotification"
    class="tw:w-full tw:bg-orange-600 tw:px-4 tw:py-2 tw:flex tw:items-center tw:justify-center tw:gap-4"
  >
    <span class="tw:text-white tw:body-1 tw:line-clamp-2">
      {{ notificationMessage }}
    </span>

    <EdroneButton size="medium" @click="goToSendingSettings">
      {{ trans._notifications.fixNow }}
    </EdroneButton>
  </div>
</template>

<script lang="ts" setup>
import EdroneButton from "edrone-components/EdroneButton.vue";
import { computed, type ComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@tanstack/vue-query";
import { getSendersV2 } from "vue-app/api/engagements/engagements";
import type { DTOEngagementSender } from "vue-app/api/dto";
import { EDRONE_SENDING_DOMAIN } from "vue-app/types/enums";

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const router = useRouter();
const route = useRoute();

const trackerEmailSenders = window.EDRONE_CONTEXT.tracker.trackerEmailSenders ?? [];
const creationDateValue = window.EDRONE_CONTEXT.tracker.creationDate;
const trackerCreationDate = new Date(Number(creationDateValue) || Date.parse(creationDateValue));
const isTrackerOlderThan30Days = Date.now() - trackerCreationDate.getTime() > THIRTY_DAYS_MS;

const { data: sendersData } = useQuery({
  queryKey: ["engagementSendersNotification"],
  queryFn: getSendersV2,
  enabled: isTrackerOlderThan30Days && trackerEmailSenders.length > 0,
});

const isUsingEdroneDomain: ComputedRef<boolean> = computed(() => {
  return trackerEmailSenders.some((sender) => sender.fromEmail?.includes(EDRONE_SENDING_DOMAIN));
});

const isUsingUnverifiedDomain: ComputedRef<boolean> = computed(() => {
  if (!sendersData.value?.senders) return false;

  const engagementSenders = sendersData.value.senders as DTOEngagementSender[];

  return trackerEmailSenders.some((trackerSender) => {
    if (!trackerSender.fromEmail) return false;
    if (trackerSender.fromEmail.includes(EDRONE_SENDING_DOMAIN)) return false;

    const matchingEngSender = engagementSenders.find((es) => es.fromEmail === trackerSender.fromEmail && !es.deleted);

    return matchingEngSender ? !matchingEngSender.allRecordsVerified : false;
  });
});

const isOnExcludedPage: ComputedRef<boolean> = computed(() => {
  return route.path.startsWith("/app/account") || route.path.startsWith("/app/settings/sending-domain");
});

const shouldShowNotification: ComputedRef<boolean> = computed(() => {
  if (window.EDRONE_CONTEXT.isDemo) return false;
  if (isOnExcludedPage.value) return false;
  if (!isTrackerOlderThan30Days) return false;
  if (trackerEmailSenders.length === 0) return false;

  return isUsingEdroneDomain.value || isUsingUnverifiedDomain.value;
});

const notificationMessage: ComputedRef<string> = computed(() => {
  if (isUsingUnverifiedDomain.value) {
    return window._trans._notifications.emailSenderUnverified;
  }
  return window._trans._notifications.emailSenderEdroneDomain;
});

const goToSendingSettings = () => {
  router.push({ name: "PageAccountSendingSettings" });
};
</script>
